import { useMutation, useQuery } from '@apollo/client/react';
import { Button } from '../ui/button';
import {
  GET_CURRENT_USER_CALENDAR,
  CONNECT_GOOGLE_CALENDAR,
  DISCONNECT_GOOGLE_CALENDAR,
} from '../../graphql/appointments';

interface CurrentUserCalendar {
  id: string;
  googleCalendarConnected: boolean;
  googleCalendarLastSync?: string;
}

interface GetCurrentUserCalendarData {
  getCurrentUser: CurrentUserCalendar;
}

interface ConnectGoogleCalendarData {
  connectGoogleCalendar: {
    authUrl: string;
  };
}

interface GoogleCalendarSettingsProps {
  onClose: () => void;
}

export function GoogleCalendarSettings({ onClose }: GoogleCalendarSettingsProps) {
  const { data, loading, refetch } = useQuery<GetCurrentUserCalendarData>(GET_CURRENT_USER_CALENDAR);

  const [connectGoogleCalendar, { loading: connecting }] = useMutation<ConnectGoogleCalendarData>(
    CONNECT_GOOGLE_CALENDAR
  );

  const [disconnectGoogleCalendar, { loading: disconnecting }] = useMutation(
    DISCONNECT_GOOGLE_CALENDAR,
    {
      refetchQueries: [{ query: GET_CURRENT_USER_CALENDAR }],
    }
  );

  const user = data?.getCurrentUser;
  const isConnected = user?.googleCalendarConnected || false;
  const lastSync = user?.googleCalendarLastSync;

  const handleConnect = async () => {
    try {
      const result = await connectGoogleCalendar();
      const authUrl = result.data?.connectGoogleCalendar?.authUrl;

      if (authUrl) {
        // Open OAuth URL in new window
        window.open(authUrl, '_blank', 'width=600,height=700');

        // Poll for connection status
        const pollInterval = setInterval(async () => {
          const { data: updatedData } = await refetch();
          if (updatedData?.getCurrentUser?.googleCalendarConnected) {
            clearInterval(pollInterval);
            alert('Google Calendar connected successfully!');
          }
        }, 2000);

        // Stop polling after 2 minutes
        setTimeout(() => clearInterval(pollInterval), 120000);
      }
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      alert('Failed to connect Google Calendar. Please try again.');
    }
  };

  const handleDisconnect = async () => {
    if (
      !confirm(
        'Are you sure you want to disconnect Google Calendar? Your appointments will no longer sync.'
      )
    ) {
      return;
    }

    try {
      await disconnectGoogleCalendar();
      alert('Google Calendar disconnected successfully.');
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      alert('Failed to disconnect Google Calendar. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Google Calendar Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
            </div>
          ) : (
            <>
              {/* Connection Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        isConnected ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                    <div>
                      <p className="font-medium text-gray-900">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </p>
                      {isConnected && lastSync && (
                        <p className="text-sm text-gray-600">
                          Last synced:{' '}
                          {new Date(lastSync).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  {isConnected ? (
                    <svg
                      className="w-8 h-8 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Information */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">How it works</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      Appointments you create will automatically appear in your Google Calendar
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      Updates and deletions in this system sync to Google Calendar
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      If you delete an appointment in Google Calendar, it will be removed from this system
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      This is optional - the appointment system works fully without Google Calendar
                    </span>
                  </li>
                </ul>
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-gray-200">
                {isConnected ? (
                  <Button
                    variant="destructive"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="w-full"
                  >
                    {disconnecting ? 'Disconnecting...' : 'Disconnect Google Calendar'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleConnect}
                    disabled={connecting}
                    className="w-full"
                  >
                    {connecting ? 'Connecting...' : 'Connect Google Calendar'}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
